import { apiRequest } from "@/shared/lib/apiClient";
import type {
  CompanyAdmissionApi,
  CompanyAdmissionDetail,
  CompanyAdmissionsQuery,
  CompanyBillingInvoice,
  CompanyInvoiceDetail,
  HospitalDashboardApiResponse,
} from "./types";

function buildAdmissionsQuery(params: CompanyAdmissionsQuery): string {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set("status", params.status);
  if (params.date_start) searchParams.set("date_start", params.date_start);
  if (params.date_end) searchParams.set("date_end", params.date_end);
  if (params.search) searchParams.set("search", params.search);
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchHospitalDashboard(): Promise<HospitalDashboardApiResponse> {
  return apiRequest<HospitalDashboardApiResponse>("/claims/dashboard", { auth: true });
}

export type HospitalInvoicesQuery = {
  status?: "submitted" | "inReview" | "approved";
};

export async function fetchHospitalInvoices(
  query: HospitalInvoicesQuery = {},
): Promise<CompanyBillingInvoice[]> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);

  const qs = params.toString();
  const response = await apiRequest<{ invoices: CompanyBillingInvoice[] }>(
    `/claims/invoices${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.invoices ?? [];
}

export async function fetchHospitalInvoice(id: number): Promise<CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: CompanyInvoiceDetail }>(`/claims/invoices/${id}`, {
    auth: true,
  });
  return response.invoice;
}

export async function fetchHospitalAdmissions(
  query: CompanyAdmissionsQuery = {},
): Promise<{ admissions: CompanyAdmissionApi[]; total: number }> {
  return apiRequest<{ admissions: CompanyAdmissionApi[]; total: number }>(
    `/claims/admissions${buildAdmissionsQuery(query)}`,
    { auth: true },
  );
}

export async function fetchHospitalAdmission(id: number): Promise<CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: CompanyAdmissionDetail }>(
    `/claims/admissions/${id}`,
    { auth: true },
  );
  return response.admission;
}

export type HospitalProfileApi = {
  id: number;
  name: string;
  address: string;
  phone: string;
};

export type UpdateHospitalProfilePayload = {
  name: string;
  address?: string;
  phone?: string;
};

export type EmployeeLookupFamilyMember = {
  id: number;
  name: string;
  relationship: string;
};

export type EmployeeLookupResult = {
  employee: {
    id: number;
    matricule: string;
    employee_name: string;
    company_id: number;
    company_name: string;
  };
  family_members: EmployeeLookupFamilyMember[];
};

export async function lookupHospitalEmployeeByMatricule(
  matricule: string,
  companyId?: number,
): Promise<EmployeeLookupResult> {
  const params = new URLSearchParams({ matricule: matricule.trim() });
  if (companyId && companyId > 0) params.set("company_id", String(companyId));
  return apiRequest<EmployeeLookupResult>(`/claims/employees/lookup?${params.toString()}`, {
    auth: true,
  });
}

export async function fetchHospitalCompanyEmployees(
  companyId: number,
  search?: string,
): Promise<Array<{ id: number; name: string; system_id: string; company_id: number }>> {
  const params = new URLSearchParams({ company_id: String(companyId) });
  if (search?.trim()) params.set("search", search.trim());
  const result = await apiRequest<{
    employees: Array<{ id: number; name: string; system_id: string; company_id: number }>;
  }>(`/claims/employees?${params.toString()}`, { auth: true });
  return result.employees ?? [];
}

export async function fetchHospitalEmployeeFamilyMembers(
  employeeId: number,
  companyId?: number,
): Promise<EmployeeLookupFamilyMember[]> {
  const params = new URLSearchParams({ employee_id: String(employeeId) });
  if (companyId && companyId > 0) params.set("company_id", String(companyId));
  const result = await apiRequest<EmployeeLookupResult>(
    `/claims/employees/lookup?${params.toString()}`,
    { auth: true },
  );
  return result.family_members ?? [];
}

export type CreateHospitalAdmissionPayload = {
  company_id: number;
  employee_id: number;
  employee_matricule: string;
  patient_nom: string;
  patient_prenom: string;
  patient_type: "Employé" | "Ayant droit";
  date_admission: string;
  motif_admission?: string;
  patient_id?: number | null;
};

export async function createHospitalAdmission(
  payload: CreateHospitalAdmissionPayload,
): Promise<CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: CompanyAdmissionDetail; message?: string }>(
    "/claims/admissions",
    {
      method: "POST",
      auth: true,
      body: payload,
    },
  );
  return response.admission;
}

export async function updateHospitalAdmissionStatus(
  id: number,
  status: "inProgress" | "completed",
): Promise<CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: CompanyAdmissionDetail; message?: string }>(
    `/claims/admissions/${id}/status`,
    {
      method: "PATCH",
      auth: true,
      body: { status },
    },
  );
  return response.admission;
}

export type HospitalCompanyOption = {
  id: number;
  name: string;
};

export type HospitalConventionOption = {
  id: number;
  code?: string;
  nom?: string;
  convention_code?: string;
  convention_name?: string;
  type_traitement?: string;
  mode_traitement_label?: string;
  montant_forfait?: number;
  ticket_moderateur?: number;
  natures_autorisees?: string[];
  forfait_preview?: {
    nb_familles?: number;
    nb_employes?: number;
    tarif_unitaire?: number;
    tarif_client?: number;
    montant_calcule?: number;
    effectif_scope?: string;
  };
};

export type SubmitHospitalInvoicePayload = {
  company_id: number;
  convention_id?: number;
  periode_debut: string;
  periode_fin: string;
  nature_facture?: "forfait_mensuel" | "hors_forfait";
  total_amount?: number;
  montant_rssp?: number;
  montant_prise_en_charge?: number;
  invoice_currency?: "USD" | "CDF";
  exchange_rate_cdf?: number;
  reference_prestataire?: string;
  submission_notes?: string;
  forfait_nb_familles?: number;
  forfait_nb_familles_client?: number;
  invoice_file?: File | null;
};

export async function submitHospitalInvoice(
  payload: SubmitHospitalInvoicePayload,
): Promise<CompanyInvoiceDetail> {
  const form = new FormData();
  form.append("company_id", String(payload.company_id));
  if (payload.convention_id) form.append("convention_id", String(payload.convention_id));
  form.append("periode_debut", payload.periode_debut);
  form.append("periode_fin", payload.periode_fin);
  if (payload.nature_facture) form.append("nature_facture", payload.nature_facture);
  if (payload.total_amount != null) form.append("total_amount", String(payload.total_amount));
  if (payload.montant_rssp != null) form.append("montant_rssp", String(payload.montant_rssp));
  if (payload.montant_prise_en_charge != null) {
    form.append("montant_prise_en_charge", String(payload.montant_prise_en_charge));
  }
  if (payload.invoice_currency) form.append("invoice_currency", payload.invoice_currency);
  if (payload.exchange_rate_cdf != null && payload.exchange_rate_cdf > 0) {
    form.append("exchange_rate_cdf", String(payload.exchange_rate_cdf));
  }
  if (payload.reference_prestataire) {
    form.append("reference_prestataire", payload.reference_prestataire);
  }
  if (payload.submission_notes) form.append("submission_notes", payload.submission_notes);
  if (payload.forfait_nb_familles) {
    form.append("forfait_nb_familles", String(payload.forfait_nb_familles));
  }
  if (payload.forfait_nb_familles_client) {
    form.append("forfait_nb_familles_client", String(payload.forfait_nb_familles_client));
  }
  if (payload.invoice_file) form.append("invoice_file", payload.invoice_file);

  const response = await apiRequest<{ invoice: CompanyInvoiceDetail }>("/claims/invoices", {
    method: "POST",
    auth: true,
    body: form,
  });
  return response.invoice;
}

export type HospitalMedicalActApi = {
  id: number;
  hospital_id: number;
  code: string;
  label: string;
  libelle: string;
  cost: number;
  cost_usd: number;
  care_type: string;
  care_category: string;
  pending_validation?: boolean;
  created_at?: string;
  delay_days?: number;
};

export type HospitalMedicalActPayload = {
  code: string;
  label?: string;
  libelle?: string;
  cost?: number;
  cost_usd?: number;
  care_type?: string;
  care_category?: string;
};

export async function fetchHospitalMedicalActs(query: {
  q?: string;
} = {}): Promise<{ total: number; medical_acts: HospitalMedicalActApi[] }> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; medical_acts: HospitalMedicalActApi[] }>(
    `/claims/medical-acts${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function createHospitalMedicalAct(
  payload: HospitalMedicalActPayload,
): Promise<HospitalMedicalActApi> {
  const response = await apiRequest<{ medical_act: HospitalMedicalActApi; message?: string }>(
    "/claims/medical-acts",
    { method: "POST", auth: true, body: payload },
  );
  return response.medical_act;
}

export async function updateHospitalMedicalAct(
  id: number,
  payload: HospitalMedicalActPayload,
): Promise<HospitalMedicalActApi> {
  const response = await apiRequest<{ medical_act: HospitalMedicalActApi; message?: string }>(
    `/claims/medical-acts/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.medical_act;
}

export async function deleteHospitalMedicalAct(id: number): Promise<void> {
  await apiRequest<{ message?: string }>(`/claims/medical-acts/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export type HospitalActsImportResult = {
  imported: number;
  duplicates: number;
  errors: string[];
  message?: string;
};

export async function importHospitalMedicalActsCsv(file: File): Promise<HospitalActsImportResult> {
  const form = new FormData();
  form.append("csv_file", file);
  return apiRequest<HospitalActsImportResult>("/claims/medical-acts/import", {
    method: "POST",
    auth: true,
    body: form,
  });
}

export type HospitalActsReviewStats = {
  toValidate: number;
  classified: number;
  manuallyValidated: number;
  lowConfidence: number;
  unclassified: number;
};

export type HospitalActsReviewAct = {
  id: number;
  hospital_id: number;
  code: string;
  label: string;
  libelle?: string;
  care_category: string;
  care_category_source?: string;
  care_category_confidence?: number | null;
  filter_status?: string;
  hospital?: string;
};

export async function reviewHospitalMedicalActs(query: {
  filter?: string;
  q?: string;
} = {}): Promise<{ stats: HospitalActsReviewStats; medical_acts: HospitalActsReviewAct[] }> {
  const params = new URLSearchParams();
  if (query.filter) params.set("filter", query.filter);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ stats: HospitalActsReviewStats; medical_acts: HospitalActsReviewAct[] }>(
    `/claims/medical-acts/review${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function classifyHospitalMedicalActs(
  options: { force?: boolean; limit?: number } = {},
): Promise<{ classified: number; skipped: number; message?: string; truncated?: boolean }> {
  return apiRequest<{ classified: number; skipped: number; message?: string; truncated?: boolean }>(
    "/claims/medical-acts/classify",
    { method: "POST", auth: true, body: options },
  );
}

export async function updateHospitalActCategory(
  id: number,
  payload: { action: "confirm" | "correct"; care_category?: string },
): Promise<HospitalActsReviewAct> {
  const response = await apiRequest<{ medical_act: HospitalActsReviewAct; message?: string }>(
    `/claims/medical-acts/${id}/category`,
    { method: "PATCH", auth: true, body: payload },
  );
  return response.medical_act;
}

export type HospitalDiagnosis = {
  id: number;
  hospital_id: number;
  code: string;
  name: string;
  created_by?: number | null;
  created_at?: string | null;
};

export type { CompanyAdmissionApi, CompanyAdmissionDetail };

export type HospitalPharmacyProduct = {
  id: number;
  provider_id: number;
  code: string;
  label: string;
  unit_price_usd: number;
  status: string;
};

